import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Button, Container, Paper, Stack, TextField, Typography } from "@mui/material";
import logo from "../../assests/logo.png";
import ellipse1 from "../../assests/Ellipse 1.png";
import ellipse2 from "../../assests/Ellipse 2.png";
import './Login.css';
import { showConfirmation, showSignupPopup } from "../../hooks/swalAlerts";
import useFetchIp from '../../hooks/useFetchIp';
import useRecaptcha from '../../hooks/useRecaptcha';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const Login = () => {
    const { ip } = useFetchIp();
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const [formData, setFormData] = useState({ employee_id: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [errors, setError] = useState('');
    const { executeRecaptcha } = useRecaptcha();
    const [isHovered, setIsHovered] = useState(false);

    // Redirect if user is already logged in
    useEffect(() => {
        if (user) {
            if (user.role_id.role_name === 'Admin') {
                navigate('/admin-dashboard');
            } else if (user.role_id.role_name === 'Institution') {
                navigate('/institute-dashboard');
            } else {
                navigate('/dashboard');
            }
        }
    }, [user, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const token = await executeRecaptcha('login');
            let loginResponse = await login(formData.employee_id, formData.password, token, ip);
            if (loginResponse.user.role_id.role_name === 'Admin') {
                navigate('/admin-dashboard');
            } else {
                navigate('/dashboard');
            }
        } catch (error) {
            setError(error);
            setTimeout(() => {
                setError('');
            }, 2000)
        } finally {
            setLoading(false);
        }
    };

    const handleSignupClick = async () => {
        // const proceed = await showSignupPopup();

        // if (proceed) {
            navigate("/register");
        // }
    };

    return (
        <Box sx={{
            minHeight: "100vh", width: "100%", position: "relative", background: "linear-gradient(to right, #010B2C, #0A1642)", display: "flex",
            flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "center", padding: { xs: 2, sm: 4 }, overflow: 'auto',
        }} >
            {/* Background elements */}
            <Box component="img" src={ellipse1} alt="Background circle" sx={{
                position: "absolute", width: { xs: "100%", md: "840px" },
                height: "auto", top: 0, right: 0, zIndex: 0,
            }} />
            <Box component="img" src={ellipse2} alt="Background circle" sx={{
                position: "absolute", width: { xs: "100%", md: "810px" },
                height: "auto", bottom: 0, left: 0, zIndex: 0,
            }} />
            {/* Logo */}
            <Box component="img" src={logo} alt="Second Education Logo"
                sx={{
                    position: { xs: "relative", md: "absolute" }, width: { xs: "150px", sm: "225px" }, height: "auto",
                    top: { xs: "auto", md: "49px" }, left: { xs: "auto", md: "102px" }, marginBottom: { xs: 4, md: 0 }, zIndex: 1,
                }} />
            {/* Welcome Text */}
            <Typography variant="h1" sx={{
                position: { xs: "relative", md: "absolute" }, width: { xs: "100%", md: "628px" }, top: { xs: "auto", md: "538px" },
                left: { xs: "auto", md: "116px" }, fontFamily: "'Poppins', Helvetica", fontWeight: 200, fontSize: { xs: "32px", sm: "42px", md: "52px" },
                lineHeight: "normal", background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.44) 100%)", backgroundClip: "text",
                WebkitBackgroundClip: "text", color: "transparent", textAlign: { xs: "center", md: "left" }, marginBottom: { xs: 4, md: 0 }, zIndex: 1,
            }} >
                Welcome, <br /> Start your journey <br /> now with our Learning management  <br /> system!
            </Typography>
            {/* Login Form Container */}
            <Paper
                elevation={0}
                sx={{
                    position: { xs: "relative", md: "absolute" },
                    width: { xs: "100%", sm: "80%", md: "569px" },
                    maxWidth: "569px",
                    height: "auto",
                    minHeight: { xs: "auto", md: "765px" },
                    top: { xs: "auto", md: "145px" },
                    right: { xs: "auto", md: "114px" },
                    borderRadius: "12px",
                    border: "2px solid white",
                    background: "rgba(202, 202, 202, 0.23)",
                    backdropFilter: "blur(2px)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: { xs: "40px 20px", sm: "60px 40px", md: "69px 56px" },
                    zIndex: 1,
                }}
            >
                <Container maxWidth="sm">
                    <Stack spacing={{ xs: 6, md: 13 }} width="100%">
                        <Typography
                            variant="h4"
                            align="center"
                            sx={{
                                fontFamily: "'Inter', Helvetica",
                                fontWeight: 600,
                                color: "white",
                                fontSize: { xs: "24px", sm: "28px", md: "32px" },
                            }}
                        >
                            Login to your account
                        </Typography>

                        {errors && (
                            <Typography align="center" sx={{ color: 'yellow', mb: 2 }} >
                                {errors}
                            </Typography>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={{ xs: 4, md: 8 }} width="100%">
                                <Stack spacing={3} width="100%">
                                    {/* Enrollment Field */}
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: "#DBD6D6",
                                                fontFamily: "'Inter', Helvetica",
                                                fontSize: { xs: "14px", md: "16px" },
                                                marginBottom: 1,
                                            }}
                                        >
                                            Enrollment number / Employee ID
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            name="employee_id"
                                            value={formData.employee_id}
                                            onChange={handleChange}
                                            required
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    height: { xs: "40px", md: "45px" },
                                                    background: "rgba(255, 255, 255, 0.27)",
                                                    borderRadius: "8px",
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    color: "white", // This sets the text color to white
                                                },
                                            }}
                                        />
                                    </Box>
                                    {/* Password Field */}
                                    <Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                marginBottom: 1,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#DBD6D6",
                                                    fontFamily: "'Inter', Helvetica",
                                                    fontSize: { xs: "14px", md: "16px" },
                                                }}
                                            >
                                                Password
                                            </Typography>

                                        </Box>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            variant="outlined"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    height: { xs: "40px", md: "45px" },
                                                    background: "rgba(255, 255, 255, 0.27)",
                                                    borderRadius: "8px",
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                },
                                                "& .MuiOutlinedInput-input": {
                                                    color: "white", // This sets the text color to white
                                                },
                                            }}
                                        />
                                    </Box>
                                    {/* <RouterLink
                                        to="/forgot-password"
                                        style={{
                                            color: "white",
                                            fontFamily: "'Inter', Helvetica",
                                            fontSize: "18px",
                                            textDecoration: "none",
                                            textAlign: "left",
                                        }}
                                    >
                                        Forgot Password?
                                    </RouterLink> */}



                                    <RouterLink
                                        to="/forgot-password"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            color: isHovered ? "#8B9BFF" : "#5B6CFF",
                                            fontFamily: "'Inter', Helvetica, sans-serif",
                                            fontSize: "18px",
                                            fontWeight: 500,
                                            textDecoration: "underline",
                                            textUnderlineOffset: "6px",
                                            transition: "all 0.3s ease",
                                        }}
                                        onMouseEnter={() => setIsHovered(true)}
                                        onMouseLeave={() => setIsHovered(false)}
                                    >
                                        <LockOutlinedIcon
                                            sx={{
                                                fontSize: 28,
                                                color: isHovered ? "#8B9BFF" : "#5B6CFF",
                                                transition: "all 0.3s ease",
                                            }}
                                        />
                                        Forgot Password?
                                    </RouterLink>
                                </Stack>
                                {/* Login Button */}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    type="submit"
                                    disabled={loading}
                                    sx={{
                                        height: { xs: "40px", md: "50px" },
                                        background: "#2D55FB",
                                        borderRadius: "12px",
                                        fontFamily: "'Inter', Helvetica",
                                        fontWeight: 500,
                                        fontSize: { xs: "18px", md: "22px" },
                                        textTransform: "none",
                                    }}
                                >
                                    {loading ? 'Logging in...' : 'Login now'}
                                </Button>
                            </Stack>
                        </form>
                    </Stack>
                </Container>
                {/* SNDT University Students info */}
                <Box
                    sx={{
                        width: "100%",
                        mt: { xs: 4, md: 3 },
                        p: { xs: 2, md: 2.5 },
                        borderRadius: "12px",
                        background: "rgba(232, 232, 232, 0.95)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <InfoOutlinedIcon sx={{ color: "#2D55FB", fontSize: 22 }} />
                        <Typography
                            sx={{
                                fontFamily: "'Inter', Helvetica",
                                fontWeight: 600,
                                fontSize: { xs: "15px", md: "16px" },
                                color: "#2D55FB",
                            }}
                        >
                            SNDT University Students
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: "'Inter', Helvetica",
                            fontSize: { xs: "13px", md: "14px" },
                            lineHeight: 1.6,
                            color: "#333",
                            mb: 2,
                        }}
                    >
                        Only students from SNDT Women's University can register themselves here. From the dropdown for Institute, select 'SNDT Women's University' and enroll yourself into the course. Your details will be reviewed and then accordingly course access will be assigned.
                    </Typography>
                    <Box
                        onClick={handleSignupClick}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            fontFamily: "'Inter', Helvetica",
                            fontWeight: 500,
                            fontSize: { xs: "15px", md: "16px" },
                            color: "#2D55FB",
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlineOffset: "4px",
                            "&:hover": { color: "#5B6CFF" },
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                        Register Here
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default Login;
